import { fromSpecific, TypedNode, UnknownSchema } from '@rehaje/core'
import { compile, MustacheDependency, parse } from '@rehaje/mustache'
import { reactive } from '@rehaje/reactive'
import { clone, nextTick, noop, refactor, Storage } from '@rehaje/shared'

import { RR } from '../alias'
import {
  LintMustacheCompileError,
  LintMustacheRuntimeError,
  LintMustacheSyntaxError
} from './errors'

import type { MustachePlugin } from './plugin'
import type {
  Dependency,
  SnapshotRelation,
  SnapshotSpecificLike,
  TypedSpecificLike,
  ViewScope
} from '@rehaje/core'
import type { Identifier } from '@rehaje/mustache'
import type { TRecord } from '@rehaje/shared'

export interface CompiledExpressionRunner {
  (
    variables: TRecord,
    reportError?: (error: any, range?: number[]) => void
  ): any
}

export interface ExpressionRunner {
  (variables: TRecord): any
}

const storage = new Storage('mustache')
const CacheMaxSize = 100_000
const DepsRunnerCacheKey = 'deps-runners'
const ESDepsCacheKey = 'es-deps'
const RunnerCacheKey = 'runners'

export type AnyExpressionSpecific =
  | ExpressionSpecific
  | JSExpressionSpecific
  | MustacheSpecific

export interface ExpressionSpecific extends TypedSpecificLike {
  /**
   * 表达式源码
   */
  code?: string
  /**
   * 模式
   */
  mode?: MustacheMode
  /**
   * 运行器
   * @param variables
   * @returns
   */
  runner?: ExpressionRunner
  type: 'Expression'
}

export interface JSExpressionSpecific extends TypedSpecificLike {
  /**
   * 表达式源码
   */
  code?: string
  /**
   * 模式
   */
  mode?: MustacheMode
  /**
   * 运行器
   * @param variables
   * @returns
   */
  runner?: ExpressionRunner
  type: 'JSExpression'
}

export type MustacheMode = 'normal' | 'string'

export interface MustacheSnapshotSpecific
  extends MustacheSpecific, SnapshotSpecificLike {
  dependencies: SnapshotStaticDependency[]
  type: 'Mustache'
}

export interface MustacheSpecific extends TypedSpecificLike {
  /**
   * 表达式源码
   */
  code?: string
  /**
   * 模式
   */
  mode?: MustacheMode
  /**
   * 运行器
   * @param variables
   * @returns
   */
  runner?: ExpressionRunner
  type: 'Mustache'
}

export interface SnapshotStaticDependency {
  id?: string
  identifier: Identifier
  snapshotID?: string
}

export interface UniqueStaticDependency {
  id?: string
  identifier: Identifier
  uniqueID?: string
}

export class MustacheNode extends TypedNode {
  static __r_deps_runner_cache = new Map<
    string,
    [string, CompiledExpressionRunner]
  >()

  static __r_es_deps_cache = new Map<string, Identifier[]>()
  static __r_idle_deps_runner_request: number

  static __r_idle_es_deps_request: number

  static __r_idle_runner_request: number

  static __r_promise_cache: Promise<void> | undefined

  static __r_runner_cache = new Map<string, [string, CompiledExpressionRunner]>()

  __r_deps_cache?: Map<string, TypedNode>

  __r_error_capture = this.plugin.props.enableErrorCapture

  __r_specific_runner?: ExpressionRunner

  /**
   * mustache 源码
   */
  @fromSpecific()
  @reactive()
  code?: string

  /**
   * 模式 normal: 标准模式 string: 字符串模式
   */
  @fromSpecific()
  mode?: 'normal' | 'string'

  @reactive('computed')
  get __r_dependencies(): Dependency[] {
    const self = this
    return this.__r_es_deps.map<Dependency>((dep) => ({
      invokedMethod: dep.invokedMethod,
      name: dep.name,
      get node() {
        return dep.name === 'self' ? self.__r_entity_parent : self.find(dep.name)
      },
      get path() {
        return [dep.name].concat(dep.subchain?.match(/\w+/g) ?? [])
      },
      relation: dep.invoked ? 'INVOCATION' : 'DEPENDENCY'
    }))
  }

  @reactive('computed')
  get __r_es_deps() {
    if (!this.code) return []
    const deps = MustacheNode.__r_get_es_deps(this.code)
    if (deps) return deps
    return MustacheNode.__r_set_es_deps(
      this.code,
      MustacheDependency.extract(this.estree)
    )
  }

  @reactive('computed')
  get __r_expect_schema() {
    return (
      this.__r_entity_parent?.getPropertySchemaIn(this.getEntityBelongs()) ??
      UnknownSchema
    )
  }

  @reactive('computed')
  get __r_node_deps(): UniqueStaticDependency[] {
    this.__r_deps_cache ??= new Map()
    return this.__r_es_deps
      .map((identifier) => {
        const name = identifier.name
        const node = this.find(name) ?? this.__r_deps_cache?.get(name)
        // 过滤不同树的依赖，考虑依赖被删除之后 id 会被重置，所以触发的 refactor 会存在问题
        if (node && this.isInSameTree(node)) {
          this.__r_deps_cache?.set(name, node)
          return {
            id: node?.id,
            identifier,
            uniqueID: node?.getUniqueID()
          }
        }
      })
      .filter(Boolean)
  }

  /**
   * Mustache 转义代码
   */
  @reactive('computed')
  get compiled() {
    if (this.estree) {
      return compile(this.estree, {
        reportName: '__reportError__',
        stringMode: this.isStringMode,
        withTryCatch: true
      })
    }
    return 'void 0'
  }

  @reactive('computed')
  get depsRunner() {
    if (!this.code) return noop
    const cacheKey = this.code
    let depsRunner = MustacheNode.__r_get_deps_runner(cacheKey)
    if (depsRunner) return depsRunner
    const compiled = new MustacheDependency(this.estree).compile()
    try {
      depsRunner = MustacheNode.__r_set_deps_runner(cacheKey, compiled)
    } catch {
      return noop
    }
    return depsRunner
  }

  /**
   * 当前存在的错误，如果没有那么为 undefined
   */
  get error() {
    return (
      super.getLintError(LintMustacheSyntaxError) ??
      super.getLintError(LintMustacheCompileError) ??
      super.getLintError(LintMustacheRuntimeError)
    )
  }

  /**
   * Mustache AST
   */
  @reactive('computed')
  get estree() {
    if (!this.code) return
    try {
      this.fix(LintMustacheSyntaxError)
      return parse(this.code)
    } catch (error) {
      if (error instanceof Error) {
        this.raise(new LintMustacheSyntaxError(error))
      }
    }
  }

  @reactive('computed')
  get handler() {
    return this.plugin.props.getProxyHandler?.(this)
  }

  /**
   * 判断当前表达式是否为字符串模式
   * @returns
   */
  get isStringMode() {
    if (!this.mode) {
      this.mode =
        this.__r_expect_schema.type === 'Schema.String' ? 'string' : 'normal'
    }
    return this.mode === 'string'
  }

  @reactive('computed')
  get plugin() {
    return this.getPlugin<MustachePlugin>()
  }

  /**
   * Mustache 执行器
   */
  @reactive('computed')
  get runner(): CompiledExpressionRunner {
    if (this.__r_specific_runner) return this.__r_specific_runner
    if (!this.code) return noop
    const cacheKey = `${this.code}\n//string-mode=${this.isStringMode}`
    let runner = MustacheNode.__r_get_runner(cacheKey)
    if (runner) {
      this.fix(LintMustacheSyntaxError)
      this.fix(LintMustacheCompileError)
      return runner
    }
    const compiled = this.compiled
    if (this.getLintError(LintMustacheSyntaxError)) return noop
    try {
      runner = MustacheNode.__r_set_runner(cacheKey, compiled)
      this.fix(LintMustacheCompileError)
    } catch (error) {
      this.raise(new LintMustacheCompileError(error as Error))
      return noop
    }
    return runner
  }

  constructor(specific: MustacheSpecific) {
    super(specific)
    this.__r_startAutoRefactor(specific as MustacheSnapshotSpecific)
    if (typeof specific.runner === 'function') {
      this.__r_specific_runner = specific.runner
    }
  }

  static __r_create_runner = (compiled: string) => {
    return new Function(
      '__variables__',
      '__reportError__',
      `
      with(__variables__){return (${compiled})}
      //# sourceURL=mustache/runner.js
      `
    ) as CompiledExpressionRunner
  }

  static __r_get_deps_runner = (code: string) => {
    return this.__r_deps_runner_cache.get(code)?.[1]
  }

  static __r_get_es_deps = (code: string) => {
    return this.__r_es_deps_cache.get(code)
  }

  static __r_get_runner = (code: string) => {
    return this.__r_runner_cache.get(code)?.[1]
  }

  // 1. syntax 错误(AST -> compile bug) -> 2. new Function 错误(透露) -> 3. 运行时错误

  static __r_set_deps_runner = (code: string, compiled: string) => {
    const depsRunner = this.__r_create_runner(compiled)
    this.__r_deps_runner_cache.set(code, [compiled, depsRunner])
    if (this.__r_idle_deps_runner_request) return depsRunner
    cancelIdleCallback(this.__r_idle_deps_runner_request)
    this.__r_idle_deps_runner_request = requestIdleCallback(() => {
      const cache: [string, string][] = []
      for (const [key, [compiled]] of this.__r_deps_runner_cache) {
        cache.push([key, compiled])
      }
      storage.set(DepsRunnerCacheKey, Array.from(cache))
      this.__r_idle_deps_runner_request = 0
    })

    return depsRunner
  }

  static __r_set_es_deps = (code: string, deps: Identifier[]) => {
    /**
     * deps 可能是相同的引用，需要 clone 一下再存，否则会导致缓存会串
     * 这里不用 toJS 的原因是，存在 toJS.visitor，会把 deps 数组转为对象
     */
    deps = clone(deps)
    this.__r_es_deps_cache.set(code, deps)
    if (this.__r_idle_es_deps_request) return deps
    cancelIdleCallback(this.__r_idle_es_deps_request)
    this.__r_idle_es_deps_request = requestIdleCallback(() => {
      const cache: [string, Identifier[]][] = []
      for (const [key, deps] of this.__r_es_deps_cache) {
        cache.push([key, deps])
      }
      storage.set(ESDepsCacheKey, Array.from(cache))
      this.__r_idle_es_deps_request = 0
    })
    return deps
  }

  static __r_set_runner = (code: string, compiled: string) => {
    const runner = this.__r_create_runner(compiled)
    this.__r_runner_cache.set(code, [compiled, runner])
    if (this.__r_idle_runner_request) return runner
    cancelIdleCallback(this.__r_idle_runner_request)
    this.__r_idle_runner_request = requestIdleCallback(() => {
      const cache: [string, string][] = []
      for (const [key, [compiled]] of this.__r_runner_cache) {
        cache.push([key, compiled])
      }
      storage.set(RunnerCacheKey, Array.from(cache))
      this.__r_idle_runner_request = 0
    })

    return runner
  }

  /**
   * 浏览器编译产物离线强缓存解决方案
   * 标准 async/await API(适配 react19+)
   * @example
   *  const App = ()=>{
   *   use(loadMustacheStorage())
   *   const store = useMemo(()=>createStore(dsl,{
   *     plugins:[new MustachePlugin()]
   *   }))
   * }
   */
  static loadMustacheStorage = async () => {
    const depsCache = ((await storage.get(ESDepsCacheKey)) || []) as any[]
    const runnerCache = ((await storage.get(RunnerCacheKey)) || []) as any[]
    const depsRunnerCache = ((await storage.get(DepsRunnerCacheKey)) ||
      []) as any[]
    if (depsCache.length > 0 && depsCache.length < CacheMaxSize) {
      for (const [key, deps] of depsCache) {
        this.__r_es_deps_cache.set(key, deps)
      }
    }
    if (runnerCache.length > 0 && runnerCache.length < CacheMaxSize) {
      for (const [key, compiled] of runnerCache) {
        if (typeof compiled !== 'string') continue
        this.__r_runner_cache.set(key, [compiled, this.__r_create_runner(compiled)])
      }
    }
    if (depsRunnerCache.length > 0 && depsRunnerCache.length < CacheMaxSize) {
      for (const [key, compiled] of depsRunnerCache) {
        if (typeof compiled !== 'string') continue
        this.__r_deps_runner_cache.set(key, [
          compiled,
          this.__r_create_runner(compiled)
        ])
      }
    }
  }

  /**
   * 浏览器编译产物离线强缓存解决方案
   * 适配 react16+ 使用的 suspense api
   * @example
   * const App = ()=>{
   *   useMustacheStorage()
   *   const store = useMemo(()=>createStore(dsl,{
   *     plugins:[new MustachePlugin()]
   *   }))
   * }
   */
  static useMustacheStorage() {
    if (this.__r_promise_cache) return
    this.__r_promise_cache ??= this.loadMustacheStorage()
    throw this.__r_promise_cache
  }

  __r_refactorByNodeDependency(
    refactorDeps: UniqueStaticDependency[] = []
  ): void {
    if (!this.code || !this.estree || this.__r_node_deps.length === 0) return
    this.code = refactor(
      this.code,
      this.__r_node_deps.map((nodeDep) => {
        const refactorDep = refactorDeps.find((refactorDep) => {
          return refactorDep.uniqueID === nodeDep.uniqueID
        })
        return {
          ...nodeDep.identifier,
          name: refactorDep?.id ?? nodeDep.identifier.name
        }
      }),
      { trim: true }
    )
  }

  __r_refactorBySnapshotDependency(
    snapshotDeps: SnapshotStaticDependency[],
    relation?: SnapshotRelation
  ): void {
    if (!this.code || !this.estree || this.__r_es_deps.length === 0) return
    this.code = refactor(
      this.code,
      this.__r_es_deps.map((esDep) => {
        const snapshotDep = snapshotDeps?.find((snapshotDep) => {
          return snapshotDep.id === esDep.name
        })
        return {
          ...esDep,
          name: relation?.[snapshotDep?.snapshotID ?? '']?.id ?? esDep.name
        }
      }),
      { trim: true }
    )
  }

  /**
   * 自动重构
   */
  __r_startAutoRefactor(specific: MustacheSnapshotSpecific) {
    if (specific.dependencies) {
      this.addEventListener(
        'onFromSnapshot',
        (event) => {
          this.__r_refactorBySnapshotDependency(
            specific.dependencies,
            event.detail
          )
        },
        { once: true }
      )
    }
    if (this.plugin.props.enableRefactor) {
      nextTick(() => {
        this.setReaction(
          'autoRefactor',
          () => this.__r_node_deps,
          (nodeDeps) => this.__r_refactorByNodeDependency(nodeDeps),
          { equals: isStaticDependenciesEquals }
        )
      })
    }
  }

  __r_wrapHandler(variables: TRecord) {
    return this.handler ? new Proxy(variables, this.handler) : variables
  }

  override getDependencies(): Dependency[] {
    return this.__r_dependencies
  }

  override getDependencyVariables(): string[] {
    return this.__r_es_deps.map((dep) => dep.name)
  }

  /**
   * 获取静态依赖
   */
  getESDependencies(): Identifier[] {
    return this.__r_es_deps
  }

  /**
   * 获取运行时依赖数据
   */
  getWatchDependencies(): any[] {
    try {
      // 读 variables 有隐式依赖
      const variables = this.getViewScope().dependencies
      return this.depsRunner(variables) ?? []
    } catch {
      return []
    }
  }

  override raise(error: Error) {
    if (!this.__r_error_capture) return
    if (error instanceof LintMustacheRuntimeError) {
      const isInLoop = this.__r_indexes.some((index) => +index !== 0)
      if (isInLoop) return
    }
    super.raise(error)
  }

  override toSnapshot(): MustacheSnapshotSpecific {
    return {
      ...super.toSnapshot(),
      dependencies: this.__r_node_deps.map(({ id, identifier }) => ({
        id,
        identifier,
        snapshotID: this.find(id)?.getSnapshotID()
      }))
    } as MustacheSnapshotSpecific
  }

  override transpile(scope: ViewScope, justCompute?: boolean) {
    let result: any

    const run =
      typeof this.__r_specific_runner === 'function'
        ? () => {
            try {
              result = this.runner(this.__r_wrapHandler(scope.variables))
              return result
            } catch {}
          }
        : () => {
            let hasError = false
            result = this.runner(
              this.__r_wrapHandler(scope.dependencies),
              (error, range) => {
                hasError = true
                if (error instanceof Error && range) error.range = range
                this.raise(new LintMustacheRuntimeError(error))
              }
            )
            if (!hasError) this.fix(LintMustacheRuntimeError)
            return result
          }

    // 如果不在实体节点中直接作为纯函数计算
    if (justCompute || this.__r_parent?.isEntityNode() === false) {
      run()
    } else {
      const dispose = scope.setReaction(
        'expression',
        run,
        (value) => {
          if (scope.element?.__r_unmounted) {
            /**
             * 已经卸载的元素不应该再影响到当前模型数据
             */
            dispose()
          } else {
            RR.suspendUntrack(() => {
              RR.suspendUndispatch(() => {
                /**
                 * 为什么表达式对 ViewModel 的赋值需要在 Track 和 dispatch 环境下进行？
                 *
                 * 问题一：需要 Track 环境
                 * ViewModel 上的 Ref 可能是惰性创建的（性能优化）。如果在 Untrack 环境下赋值，
                 * 此时 Ref 可能尚未创建，会导致自动类型转换失败。
                 * 注意：Untrack 环境通常是由被 Action 包装的方法引起的。
                 *
                 * 问题二：需要 dispatch 环境
                 * 即使在 createStore 阶段，赋值操作也必须在 dispatch 环境下进行，
                 * 否则同样会导致自动类型转换失败。因此这里需要使用 suspendUndispatch。
                 * 注意：Undispatch 状态通常是 createStore 阶段的性能优化包装引起的。
                 *
                 * 总结：确保赋值时同时满足 Track + dispatch 两个条件，才能保证自动类型转换正常工作。
                 */
                scope.parent?.model?.setInProperty(this.getBelongs(), value)
              })
            })
          }
        },
        { onError: (error) => this.raise(error) }
      )
    }

    return result
  }
}

function isStaticDependenciesEquals(
  next: UniqueStaticDependency[] = [],
  prev: UniqueStaticDependency[] = []
) {
  if (next.length !== prev.length) return false
  for (const [i, element] of next.entries()) {
    if (element.id !== prev[i].id) return false
  }
  return true
}

declare global {
  export interface RehajeSpecifics {
    Expression: ExpressionSpecific
    JSExpression: JSExpressionSpecific
    Mustache: MustacheSpecific
  }
  export interface RehajeTypedNodes {
    Expression: MustacheNode
    JSExpression: MustacheNode
    Mustache: MustacheNode
  }
}
